import countries, { getAlpha2Code, isValid } from 'i18n-iso-countries';
import type { ResidentialAddressDto } from '@/integrations/Immersve/types';
import type { RegionCode } from './Region';
import type {
  AddressComponent,
  ValidateAddressResponse,
} from '@/integrations/Google/AddressValidationApi/types';
import { GoogleValidationApiClient } from '@/integrations/Google/AddressValidationApi/GoogleValidationApiClient';

export type AddressType = 'structured' | 'unstructured' | 'both' | 'unknown';

export interface AddressProps {
  unitNumber?: string;
  streetNumber?: string;
  streetName?: string;
  suburb?: string;
  town?: string;
  region?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  postalCode?: string;
  fullAddress?: string;
}

export class Address {
  unitNumber: string;
  streetNumber: string;
  streetName: string;
  suburb: string;
  town: string;
  region: string;
  state: string;
  country: string;
  countryCode: string;
  postalCode: string;
  fullAddress: string;

  //eslint-disable-next-line complexity
  constructor(props: AddressProps) {
    this.unitNumber = props.unitNumber ?? '';
    this.streetNumber = props.streetNumber ?? '';
    this.streetName = props.streetName ?? '';
    this.suburb = props.suburb ?? '';
    this.town = props.town ?? '';
    this.region = props.region ?? '';
    this.state = props.state ?? '';
    this.country = props.country ?? '';
    this.countryCode = props.countryCode ?? '';
    this.postalCode = props.postalCode ?? '';
    this.fullAddress = props.fullAddress ?? '';
  }

  get addressType(): AddressType {
    return Address.calculateAddressType(this);
  }

  static calculateAddressType(address: Address): AddressType {
    const excludedProps: Array<keyof Address> = ['addressType', 'country'];

    const props: AddressProps = Object.fromEntries(
      Object.entries(address).filter((propEntry) =>
        excludedProps.every((excludedProp) => propEntry[0] !== excludedProp)
      )
    );

    const definedParts = Object.values(props).filter((s) => !!s);
    const looksLikeAnUnstructuredAddress = definedParts.length === 2 && !!props.fullAddress;
    const looksLikeAStructuredAddress = definedParts.length >= 2 && !props.fullAddress;
    const looksLikeAStructuredAddressWithAFullAddress =
      definedParts.length >= 2 && !!props.fullAddress;

    if (looksLikeAnUnstructuredAddress) {
      return 'unstructured';
    } else if (looksLikeAStructuredAddress) {
      return 'structured';
    } else if (looksLikeAStructuredAddressWithAFullAddress) {
      return 'both';
    }
    return 'unknown';
  }

  // eslint-disable-next-line complexity
  static createAddressStringFromParts(addressParts: AddressProps): string {
    const definedParts: string[] = [];

    if (addressParts?.unitNumber?.trim()) {
      definedParts.push(addressParts.unitNumber.trim());
    }
    if (addressParts?.streetNumber?.trim()) {
      definedParts.push(addressParts.streetNumber.trim());
    }
    if (addressParts?.streetName?.trim()) {
      definedParts.push(addressParts.streetName.trim());
    }
    if (addressParts?.suburb?.trim()) {
      definedParts.push(addressParts.suburb.trim());
    }
    if (addressParts?.town?.trim()) {
      definedParts.push(addressParts.town.trim());
    }
    if (
      addressParts?.region?.trim() &&
      addressParts.region.trim() !== addressParts?.state?.trim()
    ) {
      definedParts.push(addressParts.region.trim());
    }
    if (addressParts?.state?.trim()) {
      definedParts.push(addressParts.state.trim());
    }
    if (addressParts?.country?.trim()) {
      definedParts.push(addressParts.country.trim());
    }
    if (addressParts?.postalCode?.trim()) {
      definedParts.push(addressParts.postalCode.trim());
    }
    return definedParts.join(' ').trim();
  }

  static create(props?: AddressProps) {
    const countryCode = props?.country ? countries.getAlpha2Code(props.country, 'en') : '';

    let combinedRegionAndState: string | undefined;

    if (!props?.region || !props?.state || props.region === props.state) {
      combinedRegionAndState = props?.region || props?.state;
    } else {
      combinedRegionAndState = `${props?.region} ${props.state}`;
    }

    let formattedAddress = props?.fullAddress;

    if (!formattedAddress && props) {
      formattedAddress = Address.createAddressStringFromParts(props);
    }

    return new Address({
      ...props,
      fullAddress: formattedAddress,
      state: combinedRegionAndState,
      region: undefined,
      countryCode,
    });
  }

  static forGooglePlacesAddress({
    address_components,
    formatted_address,
  }: google.maps.places.PlaceResult): Address {
    const addressParts: AddressProps = {};
    //eslint-disable-next-line complexity
    address_components?.forEach((component: google.maps.GeocoderAddressComponent) => {
      // https://developers.google.com/maps/documentation/geocoding/requests-geocoding#Types
      switch (component.types[0]) {
        case 'premise':
        case 'subpremise':
          addressParts.unitNumber = component.long_name;
          break;
        case 'street_number':
          addressParts.streetNumber = component.long_name;
          break;
        case 'route':
          addressParts.streetName = component.long_name;
          break;
        case 'sublocality':
        case 'sublocality_level_1':
        case 'sublocality_level_2':
        case 'sublocality_level_3':
        case 'sublocality_level_4':
        case 'sublocality_level_5':
        case 'neighborhood':
          addressParts.suburb = component.long_name;
          break;
        case 'locality':
        case 'postal_town':
          addressParts.town = component.long_name;
          break;
        case 'administrative_area_level_2':
        case 'administrative_area_level_3':
        case 'administrative_area_level_4':
        case 'administrative_area_level_5':
        case 'administrative_area_level_6':
        case 'administrative_area_level_7':
          addressParts.region = component.long_name;
          break;
        case 'administrative_area_level_1':
          addressParts.state = component.long_name;
          break;
        case 'country':
          addressParts.country =
            countries.getName(component.short_name, 'en') || component.long_name;
          break;
        case 'postal_code':
          addressParts.postalCode = component.long_name;
          break;
      }
    });

    const countryCode = addressParts.country ? getAlpha2Code(addressParts.country, 'en') : '';

    return new Address({
      ...addressParts,
      fullAddress: formatted_address,
      countryCode,
    });
  }

  private static createDictionaryOfAddressPartsForGoogleValidationAddress(
    addressComponents: AddressComponent[]
  ) {
    //eslint-disable-next-line complexity
    return addressComponents.reduce((acc, component) => {
      let addressPartName: keyof Address | undefined;

      switch (component.componentType) {
        case 'premise':
        case 'subpremise':
          addressPartName = 'unitNumber';
          break;
        case 'street_number':
          addressPartName = 'streetNumber';
          break;
        case 'route':
          addressPartName = 'streetName';
          break;
        case 'sublocality':
        case 'sublocality_level_1':
        case 'sublocality_level_2':
        case 'sublocality_level_3':
        case 'sublocality_level_4':
        case 'sublocality_level_5':
        case 'neighborhood':
          addressPartName = 'suburb';
          break;
        case 'locality':
        case 'postal_town':
          addressPartName = 'town';
          break;
        case 'administrative_area_level_2':
        case 'administrative_area_level_3':
        case 'administrative_area_level_4':
        case 'administrative_area_level_5':
        case 'administrative_area_level_6':
        case 'administrative_area_level_7':
          addressPartName = 'region';
          break;
        case 'administrative_area_level_1':
          addressPartName = 'state';
          break;
        case 'country':
          addressPartName = 'country';
          break;
        case 'postal_code':
          addressPartName = 'postalCode';
          break;
        default:
          addressPartName = undefined;
          break;
      }

      if (addressPartName && !(addressPartName in acc)) {
        acc[addressPartName] = [];
      }
      if (addressPartName) {
        acc[addressPartName].push(component);
      }

      return acc;
    }, {});
  }

  private static assessAndSelectGoogleValidationAddressParts(
    addressComponents: AddressComponent[]
  ): AddressComponent | undefined {
    if (addressComponents.length === 0) {
      return;
    }
    if (addressComponents.length === 1) {
      return addressComponents[0];
    }
    const confirmedParts = addressComponents.filter(
      (component) => component.confirmationLevel === 'CONFIRMED'
    );

    if (confirmedParts.length === 1) {
      return confirmedParts[0];
    }

    if (confirmedParts.length > 1) {
      return Address.concatenateAddressComponentContent(confirmedParts);
    }

    return Address.concatenateAddressComponentContent(addressComponents);
  }

  static concatenateAddressComponentContent(
    addressComponents: AddressComponent[]
  ): AddressComponent {
    const concatenatedText = addressComponents
      .filter((component) => !!component.componentName)
      .map((component) => component.componentName.text)
      .join(' ');
    const firstComponent = addressComponents[0] as AddressComponent;
    return {
      componentName: {
        text: concatenatedText,
      },
      componentType: firstComponent.componentType,
    };
  }

  static forGoogleValidationAddress({
    addressComponents,
    formattedAddress,
    postalAddress,
  }: ValidateAddressResponse['result']['address']) {
    const addressComponentsByComponentType: Record<string, AddressComponent[]> =
      Address.createDictionaryOfAddressPartsForGoogleValidationAddress(addressComponents);

    const addressParts: Address = Address.create();

    // eslint-disable-next-line complexity
    Object.entries(addressComponentsByComponentType).forEach(([componentType, items]) => {
      const addressValue = Address.assessAndSelectGoogleValidationAddressParts(items);
      if (!addressValue) {
        return;
      }
      switch (componentType) {
        case 'unitNumber':
          addressParts.unitNumber = addressValue.componentName?.text;
          break;
        case 'streetNumber':
          addressParts.streetNumber = addressValue.componentName?.text;
          break;
        case 'streetName':
          addressParts.streetName = addressValue.componentName?.text;
          break;
        case 'suburb':
          addressParts.suburb = addressValue.componentName?.text;
          break;
        case 'town':
          addressParts.town = addressValue.componentName?.text;
          break;
        case 'region':
          addressParts.region = addressValue.componentName?.text;
          break;
        case 'state':
          addressParts.state = addressValue.componentName?.text;
          break;
        case 'country':
          addressParts.country =
            countries.getName(postalAddress?.regionCode, 'en') || addressValue.componentName?.text;
          break;
        case 'postalCode':
          addressParts.postalCode = addressValue.componentName?.text;
          break;
      }
    });

    addressParts.fullAddress = formattedAddress;

    return Address.create(addressParts);
  }

  static async tryParseAddressString(
    address: string,
    requiredCountryCode: string
  ): Promise<{ address: Address; hasAllParts: boolean; missingParts?: string[] }> {
    const addressData = await GoogleValidationApiClient.create().validateAddress({
      address,
      requiredCountryCode,
    });

    const hasAllParts =
      !addressData?.result?.address?.missingComponentTypes ||
      addressData.result.address.missingComponentTypes.length === 0;

    return {
      address: Address.forGoogleValidationAddress(addressData.result.address),
      hasAllParts,
      missingParts: addressData?.result?.address?.missingComponentTypes,
    };
  }

  static isEmpty(address: Address): boolean {
    const emptyAddress = Address.create();
    return JSON.stringify(address) === JSON.stringify(emptyAddress);
  }

  static forResidentialAddress(residentialAddress: ResidentialAddressDto): Address {
    return Address.create({
      unitNumber: residentialAddress.unitNumber,
      streetNumber: residentialAddress.streetNumber,
      streetName: residentialAddress.streetName,
      suburb: residentialAddress.suburb,
      town: residentialAddress.town,
      state: residentialAddress.state,
      region: residentialAddress.region,
      country: countries.getName(residentialAddress.country, 'en'),
      countryCode: residentialAddress.country,
      postalCode: residentialAddress.postalCode,
      fullAddress: residentialAddress.fullAddress,
    });
  }

  static toResidentialAddress({
    address,
    addressFormat,
  }: {
    address: Address;
    addressFormat: AddressType;
  }): ResidentialAddressDto {
    let country: RegionCode | undefined;
    if (address.countryCode?.length === 2 && isValid(address.countryCode)) {
      country = address.countryCode as RegionCode;
    } else {
      country = getAlpha2Code(address.country, 'en') as RegionCode | undefined;
    }

    if (!country) {
      throw new Error(`Failed to get Alpha2 code for country ${address.country}`);
    }

    if (addressFormat === 'unstructured') {
      return {
        fullAddress: address.fullAddress,
        country,
      };
    }
    return {
      unitNumber: address.unitNumber,
      streetNumber: address.streetNumber,
      streetName: address.streetName,
      suburb: address.suburb,
      town: address.town,
      region: address.region || address.state,
      state: address.state,
      country,
      postalCode: address.postalCode,
    };
  }
}
